/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react"
import { errorWrapInHandler } from "../services/utils/errors.service"

/**
 * Fetch the result of a promise each time the deps change.
 * Used to fetch data from the server and rerender the component when the data is available.
 * 
 * When the parameter is an id, it is stable and the setState method can be used in a form.
 *
 * Ex. const [state, setState] = usePromiseState(() => fetch('/customers/' + id), [id])
 */
export function usePromiseState<T> (
  promiseGen: () => Promise<T>,
  deps: any[],
  opts: { defaultState?: T, setErrors?: (errors: string[]) => void } = {}
): [T | undefined | null, React.Dispatch<React.SetStateAction<T | null>>] {
  const [state, setState] = useState<T | null>(opts.defaultState)
  
  useEffect(() => {
    let cancelled = false
    setState(opts.defaultState)

    void errorWrapInHandler(
      () => promiseGen().then(result => !cancelled && setState(result)),
      errors => {
        if (cancelled) return
        if (errors.length) setState(opts.defaultState)
        opts.setErrors?.(errors)
      })()

    return () => { cancelled = true }
  }, deps)

  return [state, setState]
}
